import apis from '#store/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataApiResponse, DataCollectionApiResponse } from './types';
const GENERAL_CONTACTS_KEY = 'generalContacts';

interface GeneralContactBase {
    firstName?: string;
    lastName?: string;
    gender?: string;
    age?: number;
    serviceProvided?: string;
    levelOfTreatment?: string;
    transportingUnit?: string;
    chiefComplaintId?: string;
    otherDetails?: string;
    incidentId?: string;
    locationId?: string;
    unitId?: string;
    department?: string;
}

export interface GeneralContact extends GeneralContactBase {
    id?: string;
    createdDate?: string;
    incidentName?: string;
    incidentAddress?: string;
    location?: string;
    locationAddress?: string;
    chiefComplaint?: string;
}

interface GetGeneralContactsParams {
    incidentId?: string;
    serviceProvided?: string;
    locationId?: string;
    levelOfTreatment?: string;
    age?: number;
    skip: number;
    take: number;
}

interface GetGeneralContactByIdParams {
    id: string;
}

interface CreateGeneralContactParams {
    generalContact: GeneralContact;
}

interface UpdateGeneralContactParams {
    id: string;
    generalContact: GeneralContact;
}

interface DeleteGeneralContactParams {
    id: string;
}

// Fetch general contacts list
export const useGeneralContacts = (params: GetGeneralContactsParams) => {
    return useQuery({
        queryKey: [GENERAL_CONTACTS_KEY, params],
        queryFn: async () => {
            const response = await apis.generalContacts.getGeneralContactsList({ params });
            return response.data as DataCollectionApiResponse<GeneralContact>;
        }
    });
};

// Fetch general contact by id
export const useGeneralContactById = (params: GetGeneralContactByIdParams) => {
    return useQuery({
        queryKey: [GENERAL_CONTACTS_KEY, params],
        queryFn: async () => {
            const response = await apis.generalContacts.getGeneralContactById({ data: params });
            return response.data as DataApiResponse<GeneralContact>;
        },
    });
};

// Create general contact
export const useCreateGeneralContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateGeneralContactParams) => {
            const response = await apis.generalContacts.createGeneralContact({ data: params });
            return response.data as DataApiResponse<GeneralContact>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GENERAL_CONTACTS_KEY] });
        },
    });
};

// Update general contact
export const useUpdateGeneralContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateGeneralContactParams) => {
            const response = await apis.generalContacts.updateGeneralContact({ data: params });
            return response.data as DataApiResponse<GeneralContact>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GENERAL_CONTACTS_KEY] });
        },
    });
};

// Delete general contact
export const useDeleteGeneralContact = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: DeleteGeneralContactParams) => {
            const response = await apis.generalContacts.deleteGeneralContact({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [GENERAL_CONTACTS_KEY] });
        }
    });
};